import { useState } from "react";
import { UserRoundPlus, X } from "lucide-react";
import { EditorModal } from "@/components/EditorModal";
import { api, formatApiError } from "@/lib/api";

export const PROVIDER_DISCIPLINES = [
  "Speech-Language Pathologist",
  "SLP Assistant",
  "Occupational Therapist",
  "COTA",
  "Physical Therapist",
  "School Psychologist",
  "Special Education Teacher",
  "Supervising Social Worker",
  "Registered Behavior Tech",
  "Dedicated Aide",
  "Support Professional",
];

const emptyForm = { first_name: "", last_name: "", discipline: PROVIDER_DISCIPLINES[0], email: "", phone: "", engagement_type: "1099" };

export const ProviderCreateDialog = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(emptyForm);
  const [customDiscipline, setCustomDiscipline] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const update = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await api.post("/providers", form);
      onCreated(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setSaving(false);
    }
  }

  return (
    <EditorModal onClose={onClose} testId="provider-create-modal">
      <section className="inline-edit-panel" data-testid="provider-create-dialog">
        <div className="section-heading">
          <div>
            <h2>Add provider</h2>
            <p>Creates an approved provider record immediately. Portal sign-in is granted separately from the provider's row.</p>
          </div>
          <button className="projection-icon-button" data-testid="close-provider-create" type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <form className="inline-edit-form" onSubmit={submit}>
          <label>
            First name
            <input data-testid="provider-create-first-name" maxLength={80} required type="text" value={form.first_name} onChange={update("first_name")} />
          </label>
          <label>
            Last name
            <input data-testid="provider-create-last-name" maxLength={80} required type="text" value={form.last_name} onChange={update("last_name")} />
          </label>
          <label className="inline-edit-full">
            Discipline / role
            {customDiscipline ? (
              <input data-testid="provider-create-discipline-input" maxLength={120} placeholder="e.g. Board Certified Behavior Analyst" required type="text" value={form.discipline} onChange={update("discipline")} />
            ) : (
              <select data-testid="provider-create-discipline-select" value={form.discipline} onChange={update("discipline")}>
                {PROVIDER_DISCIPLINES.map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            )}
            <button className="subtle-link" data-testid="provider-create-discipline-toggle" type="button" onClick={() => { setCustomDiscipline(!customDiscipline); setForm({ ...form, discipline: customDiscipline ? PROVIDER_DISCIPLINES[0] : "" }); }}>
              {customDiscipline ? "Choose from the list instead" : "Role not listed? Type it"}
            </button>
          </label>
          <label>
            Email
            <input data-testid="provider-create-email" required type="email" value={form.email} onChange={update("email")} />
          </label>
          <label>
            Phone (optional)
            <input data-testid="provider-create-phone" maxLength={40} type="tel" value={form.phone} onChange={update("phone")} />
          </label>
          <label className="inline-edit-full">
            Engagement type
            <select data-testid="provider-create-engagement-select" value={form.engagement_type} onChange={update("engagement_type")}>
              <option value="1099">1099 contractor (can submit invoices)</option>
              <option value="W-2">W-2 employee</option>
            </select>
          </label>
          {error && <p className="form-message error-message inline-edit-full" data-testid="provider-create-error">{error}</p>}
          <div className="board-confirm-actions inline-edit-full">
            <button className="button button-secondary" data-testid="provider-create-cancel" type="button" onClick={onClose}>Cancel</button>
            <button className="button button-primary" data-testid="provider-create-submit" disabled={saving} type="submit"><UserRoundPlus size={14} /> {saving ? "Adding…" : "Add provider"}</button>
          </div>
        </form>
      </section>
    </EditorModal>
  );
};
